import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Box } from '@mui/material';

const RoomInformation = ({ messages = [] }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [phase, setPhase] = useState('prep');
  const lastMessagesSignatureRef = useRef('');

  const typeStyles = {
    important: {
      color: 'red',
      fontWeight: 'bold',
    },
    finally: {
      color: 'red',
      fontWeight: 'bold',
    },

    info: {
      color: 'limegreen',
    },
    default: {
      color: 'white',
    },
  };

  const visibleMessage = useMemo(() => messages[currentIndex] || null, [messages, currentIndex]);
  const popupUser = useMemo(() => {
    if (!visibleMessage) return null;
    return visibleMessage.user || null;
  }, [visibleMessage]);

  useEffect(() => {
    const signature = JSON.stringify(
      messages.map((item) => ({
        type: item?.type ?? '',
        message: item?.message ?? '',
        userId: item?.user?.user_id ?? null,
        users: Array.isArray(item?.users) ? item.users.map((user) => user?.user_id ?? null) : [],
      })),
    );

    if (!messages.length) {
      lastMessagesSignatureRef.current = signature;
      setCurrentIndex(0);
      return;
    }

    if (currentIndex >= messages.length) {
      setCurrentIndex(0);
    } else if (lastMessagesSignatureRef.current && lastMessagesSignatureRef.current !== signature) {
      setCurrentIndex(0);
    }

    lastMessagesSignatureRef.current = signature;
  }, [currentIndex, messages]);

  const parseStyleString = (styleString) => {
    if (!styleString) return {};

    return styleString
      .split(';')
      .map((rule) => rule.trim())
      .filter(Boolean)
      .reduce((acc, rule) => {
        const [property, value] = rule.split(':').map((item) => item.trim());
        if (!property || !value) return acc;

        const camelCasedProperty = property.replace(/-([a-z])/g, (_, char) => char.toUpperCase());
        acc[camelCasedProperty] = value;
        return acc;
      }, {});
  };

  useEffect(() => {
    if (!messages.length) return undefined;

    const ENTER_DURATION = 1400; // メッセージのスライドイン時間
    const HOLD_DURATION = 2400; // センターで停止する時間
    const EXIT_DURATION = 1400; // スライドアウト時間
    const PREP_DELAY = 40; // スライドイン開始までのわずかなディレイ

    const timers = [];

    setPhase('prep');

    timers.push(
      setTimeout(() => {
        setPhase('enter');
      }, PREP_DELAY),
    );

    timers.push(
      setTimeout(() => {
        setPhase('hold');
      }, PREP_DELAY + ENTER_DURATION),
    );

    timers.push(
      setTimeout(() => {
        setPhase('exit');
      }, PREP_DELAY + ENTER_DURATION + HOLD_DURATION),
    );

    timers.push(
      setTimeout(() => {
        setCurrentIndex((prev) => (prev + 1) % messages.length);
      }, PREP_DELAY + ENTER_DURATION + HOLD_DURATION + EXIT_DURATION),
    );

    return () => {
      timers.forEach(clearTimeout);
    };
  }, [currentIndex, messages]);

  const messageStyle = useMemo(() => {
    const transition = phase === 'prep' ? 'none' : 'transform 0.7s ease, opacity 0.7s ease';

    const transforms = {
      prep: 'translateX(100%)',
      enter: 'translateX(0)',
      hold: 'translateX(0)',
      exit: 'translateX(-100%)',
    };

    const opacities = {
      prep: 0,
      enter: 1,
      hold: 1,
      exit: 0,
    };

    return {
      display: 'inline-block',
      whiteSpace: 'nowrap',
      transform: transforms[phase],
      opacity: opacities[phase],
      transition,
    };
  }, [phase]);

  const baseTextStyle = useMemo(
    () => ({
      ...(typeStyles[visibleMessage?.type] || typeStyles.default),
    }),
    [visibleMessage?.type],
  );

  if (!visibleMessage) {
    return null;
  }

  const renderContent = () => {
    if (Array.isArray(visibleMessage.message)) {
      return visibleMessage.message.map((segment, index) => {
        const segmentStyle = {
          ...baseTextStyle,
          ...parseStyleString(segment?.style),
        };

        return (
          <span key={`${segment.text}-${index}`} style={segmentStyle}>
            {segment.text}
          </span>
        );
      });
    }

    return <span style={baseTextStyle}>{visibleMessage.message}</span>;
  };

  const renderUserPopup = () => {
    if (!popupUser) return null;

    return (
      <Box
        sx={{
          position: 'absolute',
          left: { xs: '12px', sm: '24px' },
          bottom: '44px',
          width: { xs: 'calc(100% - 24px)', sm: '340px' },
          maxWidth: '340px',
          backgroundColor: '#fff',
          color: '#263238',
          border: '1px solid #d9e2ec',
          borderRadius: '8px',
          boxShadow: '0 10px 24px rgba(0, 0, 0, 0.18)',
          padding: '12px',
          zIndex: 1,
          whiteSpace: 'normal',
          pointerEvents: 'none',
        }}
      >
        <Box sx={{ fontSize: '12px', fontWeight: 700, color: '#52616b', mb: 0.5 }}>
          参加通知
        </Box>
        <Box sx={{ fontSize: '15px', fontWeight: 700, color: '#263238' }}>
          {popupUser.name || 'Unknown'}
        </Box>
      </Box>
    );
  };

  return (
    <Box
      sx={{
        position: 'relative',
        width: '100%',
        zIndex: 1300,
      }}
    >
      {renderUserPopup()}
      <Box
        sx={{
          overflow: 'hidden',
          whiteSpace: 'nowrap',
          backgroundColor: '#333',
          color: '#fff',
          padding: '8px 16px',
          bottom: 0,
          width: '100%',
          boxSizing: 'border-box',
        }}
      >
        <Box component="span" sx={messageStyle}>
          <span style={{ marginRight: '64px' }}>
            {renderContent()}
          </span>
        </Box>
      </Box>
    </Box>
  );
};

export default RoomInformation;

